import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ScatterChart, Scatter, ZAxis,
} from "recharts";
import { api } from "@/lib/api";
import { formatCount, formatPct, formatHMR } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";

const CAT_COLORS = { BHL: "#C4622D", EXC: "#E8884A", TEL: "#B07D1A", LDR: "#2D7A4A", MINI: "#2A5FA8" };

export default function EngineAnalytics() {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);

  useEffect(() => { api.get("/engine/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <Loading title="Engine Analytics" breadcrumb="Operations" />;

  const k = data.kpis;
  const highBandMachines = (data.scatter || [])
    .map((s) => ({ ...s, _hp: s.power_high_pct ?? null }));

  return (
    <div data-testid="engine-analytics">
      <PageHeader title="Engine Analytics" breadcrumb="Operations" />

      <div className="grid grid-cols-5 gap-4 mb-5">
        <ClickableKPITile label="Avg Utilization" value={formatPct(k.avg_utilization)} sub="Tap for top & bottom 10" color="green" onClick={() => setDrill("util")} testId="kpi-avg-util" />
        <ClickableKPITile label="Engine-On Hrs" value={formatCount(k.total_engine_on_hrs)} sub="Period total · tap for top emitters" color="copper" onClick={() => setDrill("engine-on")} testId="kpi-engine-on" />
        <ClickableKPITile label="Avg Idle %" value={formatPct(k.avg_idle_pct)} sub="Tap to inspect heatmap" color="amber" onClick={() => setDrill("idle")} testId="kpi-avg-idle" />
        <ClickableKPITile label="High Power Band" value={formatPct(k.high_power_band_pct)} sub="Of fleet >50% high · tap" color="red" onClick={() => setDrill("high-band")} testId="kpi-high-band" />
        <ClickableKPITile label="Total HMR Period" value={formatCount(k.total_hmr_period)} sub="Tap for HMR-by-age list" color="slate" onClick={() => setDrill("hmr")} testId="kpi-hmr-period" />
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* Power Band Distribution */}
        <GlassCard className="col-span-7 p-5">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Power Band Distribution by Machine Type</h3>
          <p className="text-[12px] mb-4" style={{ color: "var(--slate-500)" }}>
            Stacked %: <span style={{ color: "var(--status-green)" }}>Low</span> · <span style={{ color: "var(--status-amber)" }}>Medium</span> · <span style={{ color: "var(--status-red)" }}>High</span>. Categories &gt;50% high band are stressed.
          </p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.power_band}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="low" stackId="a" fill="#2D7A4A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="medium" stackId="a" fill="#B07D1A" />
                <Bar dataKey="high" stackId="a" fill="#B03A2A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Engine On/Off Timeline */}
        <GlassCard className="col-span-5 p-5">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Engine On vs Off — 30 Days</h3>
          <p className="text-[12px] mb-4" style={{ color: "var(--slate-500)" }}>Fleet-wide hour accumulation</p>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={data.engine_series}>
                <defs>
                  <linearGradient id="onG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C4622D" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C4622D" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
                <YAxis />
                <Tooltip />
                <Area dataKey="engine_on" stroke="#C4622D" fill="url(#onG)" strokeWidth={2} />
                <Area dataKey="engine_off" stroke="#7A726A" fill="rgba(122,114,106,0.2)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        {/* Top 10 leaderboard */}
        <GlassCard className="col-span-4 p-5" data-testid="top-10">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Top 10 Utilized</h3>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>Working time × engine-on</p>
          <Leaderboard rows={data.top10} color="green" />
        </GlassCard>

        <GlassCard className="col-span-4 p-5" data-testid="bottom-10">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Bottom 10 — Needs Attention</h3>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>Highest idle, lowest working time</p>
          <Leaderboard rows={data.bottom10} color="red" />
        </GlassCard>

        {/* Idle heatmap */}
        <GlassCard className="col-span-4 p-5">
          <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>Idle Time Heatmap</h3>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>Top machines × day of week</p>
          <HeatmapTable rows={data.heatmap} />
        </GlassCard>
      </div>

      {/* HMR Scatter */}
      <GlassCard className="p-5">
        <h3 className="font-display text-[16px] mb-1" style={{ color: "var(--slate-900)" }}>HMR Lifecycle</h3>
        <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>
          X = Age (years) · Y = Total HMR · bubble size = idle % · color = category
        </p>
        <div style={{ height: 340 }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
              <XAxis dataKey="age_years" name="Age (yr)" type="number" />
              <YAxis dataKey="total_hmr" name="HMR" />
              <ZAxis dataKey="idle_pct" range={[40, 320]} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }}
                formatter={(v, k) => k === "age_years" ? `${v}y` : k === "total_hmr" ? `${v}h` : `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {Object.keys(CAT_COLORS).map((cat) => (
                <Scatter key={cat} name={cat} data={data.scatter.filter((d) => d.category === cat)} fill={CAT_COLORS[cat]} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Drill-down panel */}
      <DrillDownPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        title={
          drill === "util" ? "Utilization Leaderboard"
          : drill === "engine-on" ? "Engine-On Hours"
          : drill === "idle" ? "Idle Time Heatmap"
          : drill === "high-band" ? "High Power Band Stress"
          : drill === "hmr" ? "Total HMR — Period"
          : ""
        }
        subtitle={
          drill === "util" ? "Top & bottom 10 machines"
          : drill === "engine-on" ? `${formatCount(k.total_engine_on_hrs)} hrs across the fleet`
          : drill === "idle" ? `Fleet average ${formatPct(k.avg_idle_pct)} · top 10 wasters by day`
          : drill === "high-band" ? `${formatPct(k.high_power_band_pct)} of fleet runs >50% high band`
          : drill === "hmr" ? `${formatCount(k.total_hmr_period)} period hours total`
          : ""
        }
        testId="engine-drilldown"
      >
        {drill === "util" && (
          <>
            <p className="text-caps font-body" style={{ marginBottom: 10, color: "var(--slate-700)" }}>Top 10</p>
            <StatTableDrillDown
              rows={data.top10}
              columns={[
                { key: "machine_id", label: "Machine", mono: true },
                { key: "model", label: "Model" },
                { key: "utilization", label: "Util", format: (v) => formatPct(v) },
                { key: "working", label: "Working", format: (v) => formatPct(v) },
              ]}
            />
            <p className="text-caps font-body" style={{ margin: "20px 0 10px", color: "var(--slate-700)" }}>Bottom 10</p>
            <StatTableDrillDown
              rows={data.bottom10}
              columns={[
                { key: "machine_id", label: "Machine", mono: true },
                { key: "model", label: "Model" },
                { key: "utilization", label: "Util", format: (v) => formatPct(v) },
                { key: "idle", label: "Idle", format: (v) => formatPct(v) },
              ]}
            />
          </>
        )}

        {drill === "engine-on" && (
          <StatTableDrillDown
            rows={[...data.top10, ...data.bottom10].sort((a, b) => b.engine_on - a.engine_on).slice(0, 15)}
            columns={[
              { key: "machine_id", label: "Machine", mono: true },
              { key: "model", label: "Model" },
              { key: "engine_on", label: "Engine On %", format: (v) => formatPct(v) },
              { key: "utilization", label: "Util", format: (v) => formatPct(v) },
            ]}
          />
        )}

        {drill === "idle" && (
          <StatTableDrillDown
            rows={data.heatmap.map((row) => {
              const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
              const total = days.reduce((s, d) => s + (row[d] || 0), 0);
              return { ...row, total: +(total / 7).toFixed(1) };
            }).sort((a, b) => b.total - a.total)}
            columns={[
              { key: "machine_id", label: "Machine", mono: true },
              { key: "total", label: "Avg Idle", format: (v) => formatPct(v) },
              { key: "Mon", label: "Mon", format: (v) => formatPct(v) },
              { key: "Sat", label: "Sat", format: (v) => formatPct(v) },
              { key: "Sun", label: "Sun", format: (v) => formatPct(v) },
            ]}
          />
        )}

        {drill === "high-band" && (
          <StatTableDrillDown
            rows={highBandMachines.sort((a, b) => (b.idle_pct || 0) - (a.idle_pct || 0)).slice(0, 20)}
            columns={[
              { key: "machine_id", label: "Machine", mono: true },
              { key: "category", label: "Category" },
              { key: "age_years", label: "Age (yr)", format: (v) => `${v}y` },
              { key: "total_hmr", label: "HMR", format: (v) => formatHMR(v) },
              { key: "idle_pct", label: "Idle", format: (v) => formatPct(v) },
            ]}
          />
        )}

        {drill === "hmr" && (
          <StatTableDrillDown
            rows={[...data.scatter].sort((a, b) => b.total_hmr - a.total_hmr).slice(0, 20)}
            columns={[
              { key: "machine_id", label: "Machine", mono: true },
              { key: "category", label: "Category" },
              { key: "age_years", label: "Age (yr)", format: (v) => `${v}y` },
              { key: "total_hmr", label: "HMR", format: (v) => formatHMR(v) },
            ]}
          />
        )}
      </DrillDownPanel>
    </div>
  );
}

function Leaderboard({ rows, color }) {
  return (
    <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
      {rows.map((r, i) => (
        <div key={r.machine_id} className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-white/50">
          <span className="font-mono text-[11px] w-5" style={{ color: "var(--slate-300)" }}>#{i + 1}</span>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[12px]" style={{ color: "var(--slate-900)" }}>{r.machine_id}</div>
            <div className="text-[10px]" style={{ color: "var(--slate-500)" }}>idle {r.idle}% · work {r.working}%</div>
          </div>
          <div
            className="font-display text-[18px]"
            style={{ color: color === "green" ? "var(--status-green)" : "var(--status-red)" }}
          >
            {r.utilization}<span className="text-[10px]">%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function HeatmapTable({ rows }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const max = 25;
  const cellBg = (v) => {
    const intensity = Math.min(1, v / max);
    return `rgba(176, 58, 42, ${0.08 + intensity * 0.55})`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr>
            <th></th>
            {days.map((d) => (
              <th key={d} className="px-1 py-1 text-center font-medium" style={{ color: "var(--slate-500)" }}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.machine_id}>
              <td className="font-mono py-1 pr-2" style={{ color: "var(--slate-700)" }}>{r.machine_id}</td>
              {days.map((d) => (
                <td key={d} className="text-center py-1.5 px-0.5">
                  <div
                    className="rounded-md text-[10px] font-mono py-1"
                    style={{ background: cellBg(r[d]), color: "var(--slate-900)" }}
                  >
                    {r[d]}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Loading({ title, breadcrumb }) {
  return (
    <div>
      <PageHeader title={title} breadcrumb={breadcrumb} />
      <div className="text-center py-20" style={{ color: "var(--slate-500)" }}>Loading…</div>
    </div>
  );
}
